import { ElementRef } from '@angular/core';
import { graphProps, IIqLoadTruckResponse } from '../../models';
import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import { DateTime } from 'luxon';

export function setUpGraph(container: ElementRef): {
    chart: am5xy.XYChart;
    root: am5.Root;
    xAxis: am5xy.DateAxis<any>;
    yAxis: am5xy.ValueAxis<any>;
} {
    const root: am5.Root = am5.Root.new(container.nativeElement, {
        tooltipContainerBounds: {
            top: 150,
            bottom: 100,
            left: 100,
            right: 100,
        },
    });
    root.utc = true;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
        }),
    );

    let xAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {}),
            baseInterval: {
                timeUnit: 'day',
                count: 1,
            },
            gridIntervals: [{ timeUnit: 'day', count: 4 }],
            periodChangeDateFormats: {
                day: 'MM/dd/yy',
            },
            dateFormats: {
                day: 'MM/dd/yy',
            },
            paddingTop: 6,
        }),
    );

    let xRenderer = xAxis.get('renderer');

    xRenderer.labels.template.setAll({
        fill: am5.color('#636D79'),
        fontSize: 12,
        rotation: 60,
        dx: 35,
        maxPosition: 0.99,
    });

    xRenderer.grid.template.setAll({
        strokeWidth: 0,
        visible: false,
    });

    // Y-Axis
    let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            numberFormat: '#a',
            renderer: am5xy.AxisRendererY.new(root, {
                stroke: am5.color('#E9ECF1'),
            }),
        }),
    );

    let yRenderer = yAxis.get('renderer');

    yRenderer.labels.template.setAll({
        fill: am5.color('#636D79'),
        textAlign: 'center',
        fontSize: 12,
        fontWeight: '300',
        paddingRight: 10,
    });

    yRenderer.grid.template.setAll({
        fill: am5.color('#E9ECF1'),
        strokeWidth: 1,
        strokeDasharray: [3, 4],
    });

    xAxis.set(
        'tooltip',
        am5.Tooltip.new(root, {
            themeTags: ['axis'],
            forceHidden: true,
        }),
    );

    return {
        chart,
        root,
        xAxis,
        yAxis,
    };
}

export async function updateGraph(
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
    ltData: Partial<IIqLoadTruckResponse>[],
    graphProps = {
        strokeColor: '#ffffff',
    },
) {
    await chart.appear(0);
    chart.series.clear();
    // X-Axis
    let tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        getStrokeFromSprite: false,
        html: `<div class="text-center p-0 m-0" >
                <p class="text-[10px] p-0 m-0">{dateTooltip}</p>
                <p class="text-[10px] p-0 m-0 font-bold">{valueY}</p>
            </div>`,
    });

    const lineSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            name: 'Load Truck Ratio Series',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'data',
            valueXField: 'date',
            stroke: am5.color(graphProps.strokeColor),
            tooltip,
        }),
    );
    lineSeries.strokes.template.setAll({
        strokeWidth: 2,
    });
    const lstt = lineSeries.get('tooltip');

    lstt.adapters.add('html', (_, target) => {
        return `
            <div class="text-center p-0 m-0" >
                <p class="text-[10px] p-0 m-0">{dateTooltip}</p>
                <p class="text-[10px] p-0 m-0 font-bold">{valueY}</p>
            </div>
        `;
    });

    lstt.events.on('globalpointermove', (evt: am5.ISpritePointerEvent) => {
        const yPos = evt?.target?.y() ?? 0;
        const height = evt?.target?.height();

        tooltip.set('pointerOrientation', 'down');
        tooltip.set('dy', -yPos - height / 2 + 15);
    });

    lstt.get('background').setAll({
        fill: am5.color('#ffffff'),
        fillOpacity: 1,
        stroke: am5.color(graphProps.strokeColor),
        strokeOpacity: 1,
    });

    // Cursor
    let cursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
        xAxis,
    });
    cursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636D79'), // dat-grey-500
    });
    cursor.lineY.setAll({
        visible: false,
    });
    chart.set('cursor', cursor);

    const data = ltData.map((response) => ({
        ...response,
        date: DateTime.fromJSDate(new Date(response.date)).toMillis(),
        dateTooltip: DateTime.fromJSDate(new Date(response.date)).toFormat('MMM dd, yyyy'),
    }));
    xAxis.data.setAll(data);
    lineSeries.data.setAll(data);

    await chart.appear(1);
}

export function renderGraph(
    container: ElementRef,
    ltData: Partial<IIqLoadTruckResponse>[],
    graphProps: graphProps = {
        strokeColor: '#ffffff',
    },
) {
    if (!container) return;
    const root = am5.Root.new(container.nativeElement, {
        tooltipContainerBounds: {
            top: 150,
            bottom: 100,
            left: 100,
            right: 100,
        },
    });

    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
        }),
    );

    // X-Axis
    let xAxis = chart.xAxes.push(
        am5xy.DateAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {}),
            baseInterval: {
                timeUnit: 'day',
                count: 1,
            },
            gridIntervals: [{ timeUnit: 'day', count: 4 }],
            periodChangeDateFormats: {
                day: 'MM/dd/yy',
            },
            dateFormats: {
                day: 'MM/dd/yy',
            },
            paddingTop: 6,
        }),
    );

    let xRenderer = xAxis.get('renderer');

    xRenderer.labels.template.setAll({
        fill: am5.color('#636D79'),
        fontSize: 12,
        rotation: 60,
        dx: 35,
        maxPosition: 0.99,
    });

    xRenderer.grid.template.setAll({
        strokeWidth: 0,
        visible: false,
    });

    // Y-Axis
    let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            numberFormat: '#a',
            renderer: am5xy.AxisRendererY.new(root, {
                stroke: am5.color('#E9ECF1'),
            }),
        }),
    );

    let yRenderer = yAxis.get('renderer');

    yRenderer.labels.template.setAll({
        fill: am5.color('#636D79'),
        textAlign: 'center',
        fontSize: 12,
        fontWeight: '300',
        paddingRight: 10,
    });

    yRenderer.grid.template.setAll({
        fill: am5.color('#E9ECF1'),
        strokeWidth: 1,
        strokeDasharray: [3, 4],
    });

    let tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        getStrokeFromSprite: false,
        html: `<div class="text-center p-0 m-0" >
                <p class="text-[10px] p-0 m-0">{categoryX.formatDate("MMMM dd, YYYY")}</p>
                <p class="text-[10px] p-0 m-0 font-bold">{valueY}</p>
            </div>`,
    });

    xAxis.set(
        'tooltip',
        am5.Tooltip.new(root, {
            themeTags: ['axis'],
            forceHidden: true,
        }),
    );

    const lineSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            name: 'Load Truck Ratio Series',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'data',
            valueXField: 'date',
            stroke: am5.color(graphProps.strokeColor),
            tooltip,
        }),
    );
    lineSeries.strokes.template.setAll({
        strokeWidth: 2,
    });
    const lstt = lineSeries.get('tooltip');

    lstt.adapters.add('html', (_, target) => {
        return `
            <div class="text-center p-0 m-0" >
                <p class="text-[10px] p-0 m-0">{dateTooltip}}</p>
                <p class="text-[10px] p-0 m-0 font-bold">{valueY}</p>
            </div>
        `;
    });

    lstt.events.on('globalpointermove', (evt: am5.ISpritePointerEvent) => {
        const yPos = evt?.target?.y() ?? 0;
        const height = evt?.target?.height();

        tooltip.set('pointerOrientation', 'down');
        tooltip.set('dy', -yPos - height / 2 + 15);
    });

    lstt.get('background').setAll({
        fill: am5.color('#ffffff'),
        fillOpacity: 1,
        stroke: am5.color(graphProps.strokeColor),
        strokeOpacity: 1,
    });

    // Cursor
    let cursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
        xAxis,
    });
    cursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636D79'), // dat-grey-500
    });
    cursor.lineY.setAll({
        visible: false,
    });
    chart.set('cursor', cursor);

    const data = ltData.map((response) => ({
        ...response,
        date: DateTime.fromJSDate(new Date(response.date)).toUTC().toMillis(),
        dateTooltip: DateTime.fromJSDate(new Date(response.date)).toFormat('MMM dd, yyyy'),
    }));

    xAxis.data.setAll(data);
    lineSeries.data.setAll(data);

    chart.appear(1);
}
